/**
 * Promotion — « **La Découpe** » (avenant **A-3**, verbe *parier*).
 *
 * Une barre = ta vidéo. Tu poses des extraits là où tu crois que les meilleurs moments sont,
 * tu les envoies sur la bonne plateforme, et à la validation la heatmap se révèle : tu vois
 * ce que tu as attrapé et ce que tu as raté. C'est un pari, pas une mesure.
 *
 * Accessible au clavier de bout en bout : le curseur de position est un `input[type=range]`,
 * chaque extrait posé a ses boutons de plateforme et son retrait.
 *
 * ⟦C57 · lot 8 · A-174⟧ **Moins de texte.** Consigne : une phrase, les nombres (durée, marqueurs, exigence), un
 * badge pour l'Usine à shorts ; la règle entière, ce que valent les marqueurs, le métrage en détail et la
 * leçon de Matt sont derrière « Les dessous ». PENDANT la pose : un compteur, un chrono, les boutons de
 * plateforme ; le métrage en jeu ne dit que « Répartis 60 % entre 3 extraits ». Bilan : les nombres, une
 * ligne par extrait ; ce que le serveur garde pour lui est derrière « Les dessous ».
 */

import { useMemo, useRef, useState } from "react";

import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { useSfx } from "../../components/SoundProvider";
import { useUIStore } from "../../stores/uiStore";
import { BlocChiffresDuTour } from "../core/BlocChiffresDuTour";
import { DessousMiniJeu } from "../core/DessousMiniJeu";
import { ligneExigence } from "../core/exigence";
import { NoteSur100 } from "../core/NoteSur100";
import {
  COMMANDES,
  CONSIGNE,
  ligneDecoupeServeur,
  ligneMetrage,
  ligneMetrageEngage,
} from "../core/textes";
import { useChronoSonore } from "../core/useChronoSonore";
import { useRafLoop } from "../core/useRafLoop";
import { useTremblementSM } from "../core/useTremblementSM";
import type { ProprietesMiniJeu } from "../core/types";
import type { TimelinePromotion } from "../../api/atelier";
import {
  AFFINITE,
  PLATEFORMES,
  depuisServeur,
  genererDecoupe,
  longueursApresMetrage,
  noterDecoupe,
  positionValide,
  type Extrait,
  type PlateformeId,
  type ResultatDecoupe,
} from "./heatmap";

/** ⟦A-69⟧ « metrage » s'intercale avant « jeu », et seulement si le nœud l'a ouverte. */
type Phase = "pret" | "metrage" | "entre" | "jeu" | "fini";

/**
 * ⟦A-69⟧ Une longueur d'extrait s'affiche en **part de la vidéo**, jamais en secondes : le jeu
 * ne modélise nulle part la durée réelle d'une vidéo, et l'inventer ici serait inventer du
 * contenu dans un écran.
 */
const part = (v: number): string => `${(v * 100).toFixed(1)} %`;

export function LaDecoupe({
  stage,
  state,
  seed,
  pattern: servi,
  onFini,
  scoresPrecedents = [],
  accueil,
  actionsPlus,
  consequence,
}: ProprietesMiniJeu) {
  const sfx = useSfx();
  /** ⟦A-76⟧ SPEC-UI §7.5 : en zone rouge de SM, la zone de jeu tremble (±1 px / 280 ms). */
  const tremblement = useTremblementSM(state);
  /** ⟦A-83⟧ `prefers-reduced-motion` : la variante posée (SPEC-UI §7.4), jumelle du serveur. */
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const pattern = useMemo(
    () =>
      servi
        ? depuisServeur(servi.patterns.promotion, state)
        : genererDecoupe({
            state,
            seed,
            scoresPrecedents,
            pose: reducedMotion,
            extraitBonus: state.skills.includes("merch") || state.skills.includes("l_agent"),
            networking: state.skills.includes("networking"),
            analytics: state.skills.includes("analytics_addict"),
            usineAShorts: state.skills.includes("usine_a_shorts"),
            phaseMetrage: state.skills.includes("usine_a_shorts"),
          }),
    [servi, state, seed, scoresPrecedents, reducedMotion],
  );

  const plan = pattern.metrage;

  const [phase, setPhase] = useState<Phase>("pret");
  const [restant, setRestant] = useState(pattern.chronoMs);
  const [curseur, setCurseur] = useState(0.2);
  const [extraits, setExtraits] = useState<Extrait[]>([]);
  const [resultat, setResultat] = useState<ResultatDecoupe | null>(null);
  const [clignote, setClignote] = useState(false);
  const [timeline, setTimeline] = useState<TimelinePromotion | null>(null);
  const ecouleRef = useRef(0);

  // ⟦A-69⟧ La phase de métrage : son propre chrono, ses propres commandes.
  const [tMet, setTMet] = useState(0);
  const [longueurs, setLongueurs] = useState<number[]>(() => [...(plan?.departs ?? [])]);
  const [tlMetrage, setTlMetrage] = useState<TimelinePromotion["metrage"]>(undefined);
  const tMetRef = useRef(0);
  const posesMetrage = useRef<NonNullable<TimelinePromotion["metrage"]>["poses"]>([]);

  useRafLoop(phase === "metrage" && plan !== null, (ecoule) => {
    tMetRef.current = ecoule;
    setTMet(ecoule);
    // §7.4 posé : « chrono retiré (soumission manuelle uniquement) » — le joueur termine.
    if (plan && ecoule >= plan.chronoMs && !pattern.pose) finirMetrage();
  });

  useRafLoop(phase === "jeu", (ecoule) => {
    ecouleRef.current = ecoule;
    setRestant(Math.max(0, pattern.chronoMs - ecoule));
    // *Analytics addict* : les zones apparaissent 800 ms toutes les 4 000 ms (§5.5).
    if (pattern.clignotent) setClignote(ecoule % 4000 < 800);
    if (ecoule >= pattern.chronoMs && !pattern.pose) valider();
  });

  // Les cinq dernières secondes se font entendre (§7.6) — pas en mode posé : pas de chrono.
  useChronoSonore(restant, phase === "jeu" && !pattern.pose);
  useChronoSonore(plan ? plan.chronoMs - tMet : 0, phase === "metrage" && !pattern.pose);

  /**
   * ⟦A-69⟧ Tailler un clip. On enregistre **la commande**, jamais la longueur retenue : le
   * serveur reborne et renormalise lui-même, et c'est lui qui a le dernier mot sur le budget.
   */
  function tailler(clip: number, longueur: number) {
    if (phase !== "metrage" || !plan) return;
    const borne = Math.max(plan.min, Math.min(plan.max, longueur));
    posesMetrage.current = [
      ...posesMetrage.current,
      { clip, atMs: Math.round(tMetRef.current), longueur: borne },
    ];
    setLongueurs((precedents) => precedents.map((v, i) => (i === clip ? borne : v)));
  }

  function finirMetrage() {
    if (!plan) return;
    const finishedAtMs = Math.min(plan.chronoMs, Math.round(tMetRef.current));
    setTlMetrage({ poses: [...posesMetrage.current], finishedAtMs });
    setPhase("entre");
  }

  /**
   * ⚠️ Les longueurs **effectives** sont celles que le serveur reconstruira, pas l'état
   * d'écran : on repasse par `longueursApresMetrage` avec la timeline soumise, exactement comme
   * `scoring.ts` le fera. Sans phase, elles valent `null` et tout retombe sur `longueur`.
   */
  const longueursEffectives = useMemo(
    () => longueursApresMetrage(pattern, tlMetrage),
    [pattern, tlMetrage],
  );
  const longueurDe = (i: number): number => longueursEffectives?.[i] ?? pattern.longueur;

  function valider() {
    // ⚠️ Un clip est une **position**, pas un geste horodaté : la timeline ne porte qu'un seul
    // instant, celui de la validation (`server/src/atelier/validate.ts#mesures`). Le métrage,
    // lui, a ses propres commandes horodatées — c'est la seule partie de cette étape qui en a.
    setTimeline({
      clips: extraits.map((e) => ({ debut: e.debut, plateforme: e.plateforme })),
      submittedAtMs: Math.min(pattern.chronoMs, Math.round(ecouleRef.current)),
      ...(tlMetrage ? { metrage: tlMetrage } : {}),
    });
    setResultat(noterDecoupe(pattern, extraits, longueursEffectives));
    setPhase("fini");
  }

  function poser(position: number) {
    if (extraits.length >= pattern.extraits) return;
    const mienne = longueurDe(extraits.length);
    const place = positionValide(pattern, position - mienne / 2, extraits, longueursEffectives);
    if (place === null) return;
    sfx("clip_place");
    setExtraits((precedents) => [...precedents, { debut: place, plateforme: "toktok" }]);
  }

  function changerPlateforme(index: number, plateforme: PlateformeId) {
    setExtraits((precedents) =>
      precedents.map((e, i) => (i === index ? { ...e, plateforme } : e)),
    );
  }

  // La heatmap complète n'existe que si le pattern la porte : en mode compte elle reste chez le
  // serveur, donc ni la révélation finale ni le clignotement d'*Analytics addict* n'ont de quoi
  // s'alimenter. On retombe sur ce qu'un assist a réellement payé (`revelees`), jamais sur rien
  // d'inventé.
  const zonesVisibles =
    pattern.zones !== null && (phase === "fini" || (pattern.clignotent && clignote))
      ? pattern.zones
      : pattern.revelees;

  if (phase === "pret") {
    return (
      <div className="jeu jeu-decoupe">
        <h3 className="jeu-titre">📣 La Découpe</h3>
        {/* ⟦C62 · friction 3⟧ La 1re fois, la réplique de Matt tient la place de la phrase de règle : un seul écran. */}
        {accueil ?? <p className="jeu-regle">{CONSIGNE.promotion.regle}</p>}
        <p className="jeu-regle jeu-commandes">{COMMANDES.promotion}</p>
        <ul className="jeu-params">
          <li>
            <strong>{pattern.extraits}</strong> extraits
          </li>
          <li>
            <strong>{(pattern.chronoMs / 1000).toFixed(0)}</strong> s
          </li>
          <li>
            <strong>{pattern.marqueurs.length}</strong> marqueur{pattern.marqueurs.length > 1 ? "s" : ""}
          </li>
          {/* ⟦C62 · friction 7⟧ « exigence E0 » est derrière « Les dessous » ; le prix d'un extrait, lui, se joue. */}
          {pattern.energieParExtrait > 0 ? (
            <li>
              <strong>{pattern.energieParExtrait}</strong> ⚡ par extrait
            </li>
          ) : null}
        </ul>
        {plan ? (
          <p className="jeu-variante" role="note">
            <span className="jeu-variante-badge">🎞️ Usine à shorts</span> {CONSIGNE.promotion.metrage}
          </p>
        ) : null}
        <div className="jeu-actions">
          {/* ⟦C62 · friction 3⟧ Un seul libellé pour lancer un mini-jeu : « Jouer ». */}
          <button
            className="bouton primaire grand"
            type="button"
            onClick={() => setPhase(plan ? "metrage" : "jeu")}
          >
            Jouer
          </button>
          {actionsPlus}
          {/* ⟦C57 · lot 8⟧ Les phrases d'avant, au mot près : la règle, les marqueurs, le métrage, puis la leçon de Matt. */}
          <DessousMiniJeu stage={stage}>
            <BlocChiffresDuTour
              lignes={[
                ligneExigence(pattern.k),
                ...(pattern.energieParExtrait > 0 ? [] : ["Extraits gratuits"]),
              ]}
            />
            <DessousBloc titre="La règle">
              <p className="dessous-texte">
                Ta vidéo, en une barre. Pose <strong>{pattern.extraits} extraits</strong> là où tu crois
                que les meilleurs moments sont, et envoie chacun sur la bonne plateforme. Les moments
                chauds sont cachés : la forme d'onde et tes marqueurs sont tes seuls indices.
              </p>
              <p className="dessous-texte">
                <strong>{pattern.marqueurs.length}</strong> marqueur{pattern.marqueurs.length > 1 ? "s" : ""}{" "}
                gagné{pattern.marqueurs.length > 1 ? "s" : ""} aux étapes d'avant.
              </p>
            </DessousBloc>
            {plan ? (
              <DessousBloc titre="Usine à shorts : le métrage">
                <p className="dessous-texte">
                  🎞️ <strong>Usine à shorts</strong> : avant de poser quoi que ce soit, tu tailles ton
                  métrage. Le total est fermé : ce que tu donnes à un short, tu le prends aux autres.
                </p>
                <p className="dessous-texte">
                  Tu as <strong>{part(plan.budget)}</strong> de ta vidéo à distribuer entre tes{" "}
                  {plan.departs.length} extraits. Un extrait long rattrape une visée fausse ; un extrait
                  court ne gaspille rien. <strong>Tu ne peux pas avoir les deux.</strong>
                </p>
              </DessousBloc>
            ) : null}
          </DessousMiniJeu>
        </div>
      </div>
    );
  }

  // ⟦A-69⟧ La phase « Le métrage » — on **répartit**, on ne pose rien encore.
  if (phase === "metrage" && plan) {
    const depense = longueurs.reduce((t, v) => t + v, 0);
    const reste = plan.budget - depense;
    return (
      <div className="jeu jeu-decoupe jeu-metrage" style={tremblement}>
        <h3 className="jeu-titre">🎞️ Le métrage</h3>
        <p className="jeu-regle">{ligneMetrage(part(plan.budget), plan.departs.length)}</p>
        <div className="montage-entete">
          <span className={`metrage-budget ${reste < 0 ? "metrage-depasse" : ""}`}>
            {reste >= 0 ? `${part(reste)} encore à placer` : `${part(-reste)} de trop`}
          </span>
          <span className="montage-restant">
            {((plan.chronoMs - tMet) / 1000).toFixed(1)} s
          </span>
        </div>

        <ul className="metrage-liste">
          {longueurs.map((longueur, i) => (
            <li key={i}>
              <span className="extrait-numero">{i + 1}</span>
              <input
                type="range"
                min={plan.min}
                max={plan.max}
                step={0.001}
                value={longueur}
                aria-label={`Longueur de l'extrait ${i + 1}`}
                onChange={(e) => tailler(i, Number(e.target.value))}
              />
              <span className="metrage-duree">{part(longueur)}</span>
              <span
                className="metrage-barre"
                aria-hidden="true"
                style={{ width: `${(longueur / plan.max) * 100}%` }}
              />
            </li>
          ))}
        </ul>

        <button className="bouton primaire grand" type="button" onClick={finirMetrage}>
          Suivant
        </button>
      </div>
    );
  }

  // ⟦A-59 pt 3⟧ L'entre-deux montre **la promesse**, jamais un chiffre : la note du métrage
  // n'existera qu'une fois les extraits posés, et inventer un score ici serait mentir.
  if (phase === "entre" && plan) {
    const depense = longueursEffectives?.reduce((t, v) => t + v, 0) ?? 0;
    return (
      <div className="jeu jeu-decoupe">
        <h3 className="jeu-titre">🎞️ Métrage taillé.</h3>
        <ul className="jeu-params">
          {(longueursEffectives ?? []).map((v, i) => (
            <li key={i}>
              extrait {i + 1} <strong>{part(v)}</strong>
            </li>
          ))}
        </ul>
        <p className="jeu-seuils">{ligneMetrageEngage(part(depense), part(plan.budget))}</p>
        <div className="jeu-actions">
          <button className="bouton primaire grand" type="button" onClick={() => setPhase("jeu")}>
            Jouer
          </button>
          {/* ⟦C57 · lot 8⟧ La phrase d'avant, mot pour mot. */}
          <LesDessous titre="Métrage taillé">
            <DessousBloc>
              <p className="dessous-texte">
                {part(depense)} de métrage engagé sur {part(plan.budget)}. Ce qui tombe à côté d'un
                moment chaud, c'est du temps mort, et ça compte pour un quart de l'étape.
              </p>
            </DessousBloc>
          </LesDessous>
        </div>
      </div>
    );
  }

  return (
    <div className="jeu jeu-decoupe" style={tremblement}>
      <div className="montage-entete">
        <span className="montage-compteur">
          {phase === "fini"
            ? pattern.zones !== null
              ? "la heatmap se révèle"
              : "extraits envoyés"
            : `${extraits.length}/${pattern.extraits} extraits posés`}
        </span>
        {phase === "jeu" && !pattern.pose ? (
          <span className="montage-restant">{(restant / 1000).toFixed(1)} s</span>
        ) : null}
        {phase === "jeu" && pattern.pose ? (
          <span className="montage-restant">Mode posé : sans chrono.</span>
        ) : null}
      </div>

      {/* La timeline : onde, zones (selon révélation), marqueurs, extraits. */}
      <div
        className="timeline"
        role={phase === "jeu" ? "button" : undefined}
        tabIndex={phase === "jeu" ? 0 : undefined}
        aria-label="Timeline de la vidéo — clique pour poser un extrait"
        onClick={
          phase === "jeu"
            ? (e) => {
                const boite = e.currentTarget.getBoundingClientRect();
                poser((e.clientX - boite.left) / boite.width);
              }
            : undefined
        }
      >
        <div className="onde" aria-hidden="true">
          {pattern.onde.map((v, i) => (
            <span key={i} style={{ height: `${Math.round(v * 100)}%` }} />
          ))}
        </div>

        {zonesVisibles.map((zone) => (
          <div
            key={`${zone.debut}`}
            className={`zone-chaude intensite-${String(zone.intensite).replace(".", "")}`}
            style={{ left: `${zone.debut * 100}%`, width: `${zone.largeur * 100}%` }}
            title={`Moment ${zone.type}`}
          >
            <span>{zone.type === "humour" ? "🤣" : zone.type === "wow" ? "✨" : "🧠"}</span>
          </div>
        ))}

        {/* ⟦A-3⟧ Les marqueurs gagnés aux étapes précédentes. */}
        {pattern.marqueurs.map((m) => (
          <span key={m} className="marqueur-atelier" style={{ left: `${m * 100}%` }} title="Un marqueur laissé par une étape réussie">
            ▾
          </span>
        ))}

        {extraits.map((extrait, i) => (
          <div
            key={i}
            className="extrait"
            style={{ left: `${extrait.debut * 100}%`, width: `${longueurDe(i) * 100}%` }}
          >
            {i + 1}
          </div>
        ))}

        {phase === "jeu"
          ? (() => {
              // ⟦A-69⟧ Le fantôme montre la longueur du **prochain** extrait, celle qu'on vient
              // de lui allouer — sinon on viserait avec une fenêtre qu'on n'a plus.
              const mienne = longueurDe(extraits.length);
              return (
                <div
                  className="extrait extrait-fantome"
                  style={{
                    left: `${Math.max(0, Math.min(1 - mienne, curseur - mienne / 2)) * 100}%`,
                    width: `${mienne * 100}%`,
                  }}
                />
              );
            })()
          : null}
      </div>

      {phase === "jeu" ? (
        <>
          <label className="curseur">
            <span className="curseur-bout">début</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.005}
              value={curseur}
              aria-label="Position de l'extrait sur la vidéo"
              onChange={(e) => setCurseur(Number(e.target.value))}
            />
            <span className="curseur-bout">fin</span>
          </label>

          <div className="decoupe-actions">
            <button
              className="bouton"
              type="button"
              disabled={extraits.length >= pattern.extraits}
              onClick={() => poser(curseur)}
            >
              ✂️ Poser un extrait
            </button>
            <button className="bouton primaire" type="button" onClick={valider}>
              Publier les extraits
            </button>
          </div>

          <ul className="extraits-liste">
            {extraits.map((extrait, i) => (
              <li key={i}>
                <span className="extrait-numero">{i + 1}</span>
                <div className="plateformes">
                  {PLATEFORMES.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`bouton ${extrait.plateforme === p.id ? "primaire" : "discret"}`}
                      onClick={() => changerPlateforme(i, p.id)}
                      title={p.pour}
                    >
                      {p.icone} {p.nom}
                    </button>
                  ))}
                </div>
                <button
                  className="bouton discret"
                  type="button"
                  onClick={() => setExtraits((e) => e.filter((_, j) => j !== i))}
                >
                  retirer
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {phase === "fini" && timeline ? (
        <>
          {resultat ? (
            <>
              <NoteSur100 note={resultat.score} consequence={consequence?.(resultat.score)} />
              <ul className="jeu-params">
                <li>
                  couverture <strong>{Math.round(resultat.couverture * 100)} %</strong>
                </li>
                <li>
                  bonne plateforme <strong>{Math.round(resultat.matching * 100)} %</strong>
                </li>
                {resultat.metrage !== null && resultat.decoupe !== null ? (
                  <li>
                    découpe <strong>{Math.round(resultat.decoupe)}</strong> · métrage{" "}
                    <strong>{Math.round(resultat.metrage.score)}</strong>
                  </li>
                ) : null}
              </ul>
              {resultat.metrage !== null ? (
                <ul className="extraits-bilan">
                  {resultat.metrage.tailles.map((t, i) => (
                    <li key={i} className={t.utile >= 0.75 ? "touche" : "rate"}>
                      Métrage {i + 1} : {part(t.longueur)} dont{" "}
                      <strong>{Math.round(t.utile * 100)} %</strong> de chaud
                      {t.utile >= 0.999
                        ? ", taillé au cordeau"
                        : t.utile === 0
                          ? ", du vide en entier"
                          : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
              <ul className="extraits-bilan">
                {resultat.detail.map((d, i) => (
                  <li key={i} className={d.recouvrement > 0 ? "touche" : "rate"}>
                    Extrait {i + 1} · {PLATEFORMES.find((p) => p.id === d.extrait.plateforme)?.nom} :{" "}
                    {d.zone === null || d.recouvrement === 0
                      ? "à côté, rien là"
                      : `${Math.round(d.recouvrement * 100)} % ${d.zone.type}${
                          d.bonnePlateforme
                            ? ", bonne plateforme"
                            : `, mauvaise plateforme (${AFFINITE[d.extrait.plateforme]} ≠ ${d.zone.type})`
                        }`}
                  </li>
                ))}
                {resultat.detail.length === 0 ? (
                  <li className="rate">Aucun extrait posé.</li>
                ) : null}
              </ul>
            </>
          ) : (
            /* Mode compte : la heatmap n'a jamais quitté le serveur. On ne montre pas un score
               qu'on n'a pas — on dit où il est. */
            <>
              <p className="score-gros">?</p>
              <p className="jeu-seuils">{ligneDecoupeServeur(timeline.clips.length)}</p>
            </>
          )}
          <div className="jeu-actions">
            <button
              className="bouton primaire grand"
              type="button"
              onClick={() => onFini(resultat?.score ?? null, timeline)}
            >
              Suivant
            </button>
            {resultat ? null : (
              <LesDessous titre="Où étaient les bons moments ?">
                <DessousBloc>
                  <p className="dessous-texte">
                    Tes {timeline.clips.length} extraits sont posés. Où étaient vraiment les bons
                    moments, seul le serveur le sait : il note cette étape et te le dira à la
                    publication.
                  </p>
                </DessousBloc>
              </LesDessous>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
